import React from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Send, Phone, Building2, FileText } from "lucide-react";
import Reveal from "../components/Reveal";
import { useLang } from "../contexts/LangContext";
import { api, formatErrorDetail } from "../lib/api";
import { PageHeader } from "./Formats";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  participants: "",
  location: "",
  message: "",
};

export default function Contatti() {
  const { t } = useLang();
  const [form, setForm] = React.useState(initial);
  const [loading, setLoading] = React.useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori (nome, email, messaggio)");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t.common.thankYou);
      setForm(initial);
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail) || t.common.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <PageHeader eyebrow={t.contact.eyebrow} title={t.contact.title} subtitle={t.contact.subtitle} />

      <section className="bg-[#0a0a0a] pb-32">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <Field label={t.contact.fields.name + " *"}>
                  <input
                    className="field"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                    data-testid="contact-name"
                  />
                </Field>
                <Field label={t.contact.fields.company}>
                  <input
                    className="field"
                    value={form.company}
                    onChange={onChange("company")}
                    data-testid="contact-company"
                  />
                </Field>
                <Field label={t.contact.fields.email + " *"}>
                  <input
                    type="email"
                    className="field"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    data-testid="contact-email"
                  />
                </Field>
                <Field label={t.contact.fields.phone}>
                  <input
                    type="tel"
                    className="field"
                    value={form.phone}
                    onChange={onChange("phone")}
                    data-testid="contact-phone"
                  />
                </Field>
                <Field label={t.contact.fields.eventType}>
                  <select
                    className="field"
                    value={form.event_type}
                    onChange={onChange("event_type")}
                    data-testid="contact-event-type"
                  >
                    <option value="">—</option>
                    {t.contact.eventTypes.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t.contact.fields.eventDate}>
                  <input
                    type="date"
                    className="field"
                    value={form.event_date}
                    onChange={onChange("event_date")}
                    data-testid="contact-event-date"
                  />
                </Field>
                <Field label={t.contact.fields.participants}>
                  <input
                    className="field"
                    value={form.participants}
                    onChange={onChange("participants")}
                    placeholder="500 - 5.000 - 50.000..."
                    data-testid="contact-participants"
                  />
                </Field>
                <Field label={t.contact.fields.location}>
                  <input
                    className="field"
                    value={form.location}
                    onChange={onChange("location")}
                    data-testid="contact-location"
                  />
                </Field>
              </div>
              <Field label={t.contact.fields.message + " *"}>
                <textarea
                  className="field"
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  required
                  data-testid="contact-message"
                />
              </Field>
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? t.common.sending : t.common.send}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={150}>
            <div className="border border-white/10 p-8 lg:p-10 sticky top-28">
              <div className="eyebrow mb-3">{t.contact.contacts}</div>
              <h3 className="h3 text-white mb-8">Scudieri Group S.r.l.</h3>
              <ul className="space-y-5 text-sm">
                <ContactRow icon={<MapPin className="w-4 h-4" />}>
                  <div className="font-display tracking-wide">Sede Operativa</div>
                  <div className="text-[color:var(--txt-secondary)] mt-1">Viale Lunigiana 8, 20125 Milano</div>
                </ContactRow>
                <ContactRow icon={<Mail className="w-4 h-4" />}>
                  <div className="font-display tracking-wide">Email</div>
                  <a href="mailto:scudierigroup@gmail.com" className="link-gold mt-1 block">
                    scudierigroup@gmail.com
                  </a>
                </ContactRow>
                <ContactRow icon={<Send className="w-4 h-4" />}>
                  <div className="font-display tracking-wide">PEC</div>
                  <div className="text-[color:var(--txt-secondary)] mt-1 break-all">
                    sushicompanysrl1@legalmail.it
                  </div>
                </ContactRow>
                <ContactRow icon={<Building2 className="w-4 h-4" />}>
                  <div className="font-display tracking-wide">P.IVA / CF</div>
                  <div className="text-[color:var(--txt-secondary)] mt-1">09395210967</div>
                </ContactRow>
                <ContactRow icon={<FileText className="w-4 h-4" />}>
                  <div className="font-display tracking-wide">REA</div>
                  <div className="text-[color:var(--txt-secondary)] mt-1">MI-2087451</div>
                </ContactRow>
              </ul>
              <div className="mt-10 pt-6 border-t border-white/10 text-xs text-[color:var(--txt-muted)] leading-relaxed">
                Risposta media entro 24/48h lavorative. Per eventi imminenti contattaci direttamente via email.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-display text-[10px] tracking-[0.32em] uppercase text-[color:var(--gold)]">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ContactRow({ icon, children }) {
  return (
    <li className="flex items-start gap-4">
      <div className="w-9 h-9 border border-[color:var(--gold)]/40 text-[color:var(--gold)] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>{children}</div>
    </li>
  );
}
